import { useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { Link, useNavigate, useParams } from "react-router";
import { useToast } from "../../components/ui/Toast";
import {
  FaqEditor,
  faqFormValuesFromRecord,
  type FaqFormValues,
} from "./faq-edit/FaqEditor";
import { DELETE_FAQ, UPDATE_FAQ } from "./faq-edit/faqMutations";
import { useFaqData } from "./faq-edit/useFaqData";

interface UpdateResponse {
  updateFaq: { id: string; question: string } | null;
}

export function FaqEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { faq, loading, error } = useFaqData(id);
  const [update, updateResult] = useMutation<UpdateResponse>(UPDATE_FAQ, {
    refetchQueries: ["AllFaqsForListing"],
  });
  const [del, delResult] = useMutation(DELETE_FAQ, {
    refetchQueries: ["AllFaqsForListing"],
  });
  const [saveError, setSaveError] = useState<string | null>(null);

  const initialValues = useMemo(
    () => (faq ? faqFormValuesFromRecord(faq) : null),
    [faq],
  );

  async function handleSave(values: FaqFormValues) {
    if (!id) return;
    setSaveError(null);
    try {
      const result = await update({
        variables: {
          id,
          question: values.question,
          answer: values.answer || null,
          sortOrder: values.sortOrder,
          showOnFaqPage: values.showOnFaqPage,
        },
      });
      const updated = result.data?.updateFaq;
      if (!updated) throw new Error("Couldn't save FAQ");
      toast.push({ kind: "success", title: "FAQ saved" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      setSaveError(message);
      toast.push({
        kind: "error",
        title: "Couldn't save FAQ",
        description: message,
      });
    }
  }

  async function handleDelete() {
    if (!id || !faq) return;
    const ok = window.confirm(
      `Delete "${faq.question}"? This removes it from /faq and any landing pages it's embedded in.`,
    );
    if (!ok) return;
    try {
      await del({ variables: { id } });
      toast.push({ kind: "success", title: "FAQ deleted" });
      navigate("/website/faqs");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      toast.push({
        kind: "error",
        title: "Couldn't delete FAQ",
        description: message,
      });
    }
  }

  if (loading && !faq) {
    return (
      <div className="px-7 py-10 text-[13px] text-ink-4">Loading FAQ…</div>
    );
  }
  if (error || !faq || !initialValues) {
    return (
      <div className="px-7 py-10">
        <div className="mb-3 text-[13px] text-rust">
          Couldn't load FAQ: {error?.message ?? "not found"}
        </div>
        <Link
          to="/website/faqs"
          className="text-[13px] text-tide hover:underline"
        >
          ← Back to FAQs
        </Link>
      </div>
    );
  }

  return (
    <FaqEditor
      mode="edit"
      initialValues={initialValues}
      isSaving={updateResult.loading}
      saveError={saveError}
      onSave={handleSave}
      onDelete={handleDelete}
      isDeleting={delResult.loading}
    />
  );
}
